import { gql } from '@apollo/client';
import {
  Avatar,
  Box,
  IconButton,
  SxProps,
  Tooltip,
  Typography,
} from '@mui/material';
import { Carousel } from 'components/common/Media/components/Carousel/Carousel';
import { IconBoldDollarCircle } from 'components/icons/components/bold/IconBoldDollarCircle';
import { IconBoldPlay } from 'components/icons/components/bold/IconBoldPlay';
import { IconCustomExpire } from 'components/icons/components/custom/IconCustomExpire';
import { IconLinearExport2 } from 'components/icons/components/linear/IconLinearExport2';
import { getCreatorHandleWithPrefix } from 'features/creator';
import {
  useInstagramStoryPostState,
  useSocialListeningPostLinkGenerator,
} from 'features/socialMediaListening';
import { HoverPlayableVideo } from 'features/socialMediaListening/views/postsGrid/postCard/HoverPlayableVideo';
import { StaticImage } from 'features/socialMediaListening/views/postsGrid/postCard/StaticImage';
import { platformIconMap } from 'features/socialPost/constants';
import {
  SocialPostFragmentSocialPostCardViewFragment,
  SocialPostFragmentUseSocialListeningPostLinkGeneratorFragmentDoc,
  SocialPostType,
} from 'graphql/generated';
import moment from 'moment';
import { useCallback } from 'react';
import { theme } from 'styles/theme';
import { formatBigNumber } from 'utils/number';

// eslint-disable-next-line
gql`
  fragment SocialPostFragmentSocialPostCardView on SocialPostModel {
    id
    platform
    thumbnailUrl
    videoUrl
    type
    carouselMedias {
      id
      type
      url
    }
    playCount
    isAd
    creator {
      id
      handle
      profilePictureUrl
    }
    ...SocialPostFragmentUseSocialListeningPostLinkGenerator
    ...SocialPostFragmentUseInstagramStoryPostState
  }
  ${SocialPostFragmentUseSocialListeningPostLinkGeneratorFragmentDoc}
`;

export type SocialPostCardViewProps = {
  socialPost: SocialPostFragmentSocialPostCardViewFragment;
  sx?: SxProps;
};

export const SocialPostCardView = (props: SocialPostCardViewProps) => {
  const { socialPost, sx } = props;

  const { generateLink } = useSocialListeningPostLinkGenerator();
  const { isPostAStory, hasExpired, isExpiringSoon, pillBgColor, timeAgo } =
    useInstagramStoryPostState({ socialPost });

  const renderActionsTopRight = useCallback(
    (isHovered: boolean) => {
      const Icon = platformIconMap[socialPost.platform];
      return (
        <Box
          display="flex"
          gap={1}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {/* if the post is a story, show the expire date pill for the story */}
          {isPostAStory ? (
            <Tooltip
              title={moment(socialPost.expiresAt).format('MMM DD, YYYY h:mm A')}
            >
              <Box
                component="button"
                sx={{
                  p: 1,
                  pr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: pillBgColor,
                  borderRadius: 50,
                }}
              >
                <IconCustomExpire
                  size={16}
                  color={theme.colors?.primary.white}
                />
                <Typography
                  variant="headline-xs"
                  color={theme.colors?.primary.white}
                >
                  {timeAgo}
                </Typography>
              </Box>
            </Tooltip>
          ) : (
            <>
              {/* if the post is not a story, show the export icon and platform icon */}
              {isHovered && socialPost.creator?.handle && (
                <IconButton
                  disableRipple
                  sx={{
                    p: 1,
                    backgroundColor: 'rgba(35, 6, 3, 0.10)',
                    color: theme.colors?.primary.white,
                    backdropFilter: 'blur(4px)',
                  }}
                  onClick={() => {
                    window.open(generateLink(socialPost) || '', '_blank');
                  }}
                >
                  <IconLinearExport2 size={16} />
                </IconButton>
              )}
              {socialPost.isAd && (
                <IconBoldDollarCircle
                  size={16}
                  color={theme.colors?.primary.white}
                />
              )}
              {Icon && (
                <Icon
                  size={16}
                  style={{
                    color: theme.colors?.primary.white,
                  }}
                />
              )}
            </>
          )}
        </Box>
      );
    },
    [socialPost, generateLink, hasExpired, isExpiringSoon, pillBgColor],
  );

  // only show the platform icon if the post is a story
  const renderExtraActionsTopLeft = useCallback(
    (isHovered: boolean) => {
      const Icon = platformIconMap[socialPost.platform];

      if (!Icon || !isPostAStory) {
        return null;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(1),
          }}
        >
          <Icon size={16} color={theme.colors?.primary.white} />
        </Box>
      );
    },
    [socialPost, isPostAStory],
  );

  const renderActionsBottom = useCallback(
    () => (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box
          display="flex"
          gap={1}
          alignItems="center"
          width="70%"
          maxWidth="70%"
        >
          <Avatar
            src={socialPost.creator?.profilePictureUrl || ''}
            sx={{
              width: theme.spacing(4),
              height: theme.spacing(4),
            }}
          />
          <Typography
            color={theme.colors?.primary.parchment}
            variant="body-lg"
            fontWeight={600}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getCreatorHandleWithPrefix(socialPost.creator?.handle ?? '')}
          </Typography>
        </Box>

        {socialPost.type === SocialPostType.Video &&
          socialPost.playCount > 0 && (
            <Box display="flex" gap={1} alignItems="center">
              <IconBoldPlay size={16} color={theme.colors?.primary.parchment} />
              <Typography
                color={theme.colors?.primary.parchment}
                variant="body-lg"
                fontWeight={600}
              >
                {formatBigNumber(socialPost.playCount || 0)}
              </Typography>
            </Box>
          )}
      </Box>
    ),
    [socialPost],
  );

  const renderContent = useCallback(() => {
    if (socialPost.type === SocialPostType.Video) {
      // play video on hover
      // but still keep click on video => navigate to post page
      return (
        <HoverPlayableVideo
          thumbnailUrl={socialPost.thumbnailUrl || ''}
          videoUrl={socialPost.videoUrl || ''}
          renderActionsTopRight={renderActionsTopRight}
          renderExtraActionsTopLeft={renderExtraActionsTopLeft}
          renderActionsBottom={renderActionsBottom}
          componentsProps={{
            topRightContainerStyle: {
              top: theme.spacing(1.5),
              right: theme.spacing(1.5),
            },
            topLeftContainerStyle: {
              top: theme.spacing(1.5),
              left: theme.spacing(1.5),
              gap: 0,
            },
            actionsBottomContainerStyle: {
              pb: theme.spacing(2),
              px: theme.spacing(2),
            },
          }}
        />
      );
    }

    if (socialPost.type === SocialPostType.Carousel) {
      return (
        <Carousel
          medias={socialPost.carouselMedias.map((media) => ({
            id: media.id,
            type: media.type,
            url: media.url,
          }))}
          fallbackUrl={socialPost.thumbnailUrl || ''}
        />
      );
    }

    return (
      <StaticImage
        thumbnailUrl={socialPost.thumbnailUrl || ''}
        renderActionsTopRight={renderActionsTopRight}
        renderActionsBottom={renderActionsBottom}
      />
    );
  }, [socialPost]);

  return (
    <Box
      sx={{
        position: 'relative',
        ...sx,
      }}
    >
      {renderContent()}
    </Box>
  );
};
